import React, { useState, useMemo, useEffect } from "react";
import { motion } from 'framer-motion';
import { Database, FileText, Download, Shield, Code, Terminal, Cpu, Zap } from 'lucide-react';

const Hero = () => {
        useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, []);

  const apis = [
    { 
      icon: Database, 
      label: 'Consulta RUC/DNI', 
      description: 'API RESTful con respuesta en JSON',
      code: 'GET /v1/ruc/{numero}'
    },
    { 
      icon: FileText, 
      label: 'Envío Facturas/Boletas', 
      description: 'SOAP y REST para documentos electrónicos',
      code: 'POST /v1/documents'
    },
    { 
      icon: Download, 
      label: 'Descarga XML/PDF/CDR', 
      description: 'Endpoints para archivos binarios',
      code: 'GET /v1/files/{ticket}'
    },
    { 
      icon: Shield, 
      label: 'Buzón & Tipo Cambio', 
      description: 'Webhooks y consultas en tiempo real',
      code: 'GET /v1/exchange-rate'
    }
  ];

  return (
    <section className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 overflow-hidden relative border-b border-gray-200">
      {/* Efectos de fondo sutiles */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Grid técnico sutil */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>
        
        {/* Puntos de conexión */}
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-500/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
            }}
            animate={{
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Badge técnico */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-2 mb-8"
          >
            <Terminal className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-mono text-blue-700">API v2.1 • STABLE</span>
          </motion.div>

          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight font-sans"
            initial={{ scale: 0.98 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
          >
            <div className="text-gray-800">SUNAT API</div>
            <motion.div 
              className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Para Desarrolladores
            </motion.div>
          </motion.h1>
          
          <motion.p 
            className="text-lg text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed font-light"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Integración directa con SUNAT mediante APIs RESTful y SOAP. Documentación completa, 
            SDKs disponibles y soporte técnico especializado para desarrolladores.
          </motion.p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <motion.button
              whileHover={{ 
                scale: 1.02,
                boxShadow: "0 8px 25px rgba(37, 99, 235, 0.15)"
              }}
              whileTap={{ scale: 0.98 }}
              className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold text-base shadow-md hover:shadow-lg transition-all duration-200 inline-flex items-center space-x-2 border border-blue-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Code size={18} />
              <span>Ver Documentación</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="border border-gray-300 text-gray-700 bg-white px-8 py-3 rounded-lg font-semibold text-base hover:border-gray-400 transition-all duration-200 inline-flex items-center space-x-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <Terminal size={18} />
              <span>Probar en Sandbox</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Cards de APIs - Estilo más técnico */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          {apis.map((api, index) => {
            const Icon = api.icon;
            return (
              <motion.div
                key={api.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + index * 0.1, duration: 0.5 }}
                className="group bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-md transition-all duration-300"
                whileHover={{ y: -2 }}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                    <Icon className="w-6 h-6 text-blue-600" />
                  </div>
                  
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold text-gray-900 text-lg mb-1">{api.label}</h3>
                    <p className="text-gray-600 text-sm mb-3">{api.description}</p>
                    
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 font-mono text-xs text-gray-700 group-hover:border-gray-300 transition-colors">
                      {api.code}
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-xs text-gray-500 font-medium">REST API • JSON</span>
                      <motion.div 
                        whileHover={{ x: 2 }}
                        className="text-blue-600 text-xs font-medium cursor-pointer hover:text-blue-700"
                      >
                        Ver ejemplos →
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Características técnicas */}
        <motion.div
          className="mt-16 pt-12 border-t border-gray-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
            {[
              { icon: Zap, label: 'Latencia <100ms', value: 'Alto rendimiento' },
              { icon: Shield, label: 'SSL/TLS 1.3', value: 'Comunicación segura' },
              { icon: Cpu, label: '99.9% Uptime', value: 'Disponibilidad garantizada' },
              { icon: Code, label: 'SDK Multi-lenguaje', value: 'Python, JS, Java, PHP' }
            ].map((feature, index) => (
              <motion.div
                key={feature.label}
                className="text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.4 + index * 0.1 }}
              >
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <feature.icon className="w-5 h-5 text-gray-600" />
                </div>
                <div className="font-semibold text-gray-900 text-sm">{feature.label}</div>
                <div className="text-gray-500 text-xs mt-1">{feature.value}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA para desarrolladores */}
        <motion.div
          className="mt-12 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
        >
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="font-semibold text-gray-900 mb-1">¿Listo para integrar?</h3>
              <p className="text-gray-600 text-sm">Regístrate y obtén tus API keys en minutos</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium text-sm border border-blue-700 hover:bg-blue-700 transition-colors"
            >
              Crear Cuenta
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;